/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import React, {useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Header, NAME_PLACEHOLDER} from "../Components/Pages/QueryTool";
import {GuidUtils, ObjectUtils} from "../Utils";
import {
    CHANGE_FAVORITE,
    DELETE_COLUMNS, FINISH_CHANGING_FAVORITES, FINISH_DELETE_COLUMNS,
    FINISH_INSERT_COLUMNS, FINISH_RESET_FILTERS, FINISH_SET_FILTER,
    INSERT_COLUMNS, RESET_FILTERS,
    SET_COLUMNS, SET_COUNT, SET_DATA, SET_FILTER,
    SET_IS_STALE,
    SET_NEW_COLUMNS, SET_PAGE, SET_SORTED
} from "../Redux/Actions";
import {service as QueryToolService} from "../Services/QueryToolService";
import {useUpdateEffect} from "./EffectHooks";
import {usePatchQueryParams} from "../../../monitoring/src/Hooks/RouteHooks"

export const useGetDefinition = () => {
    const {
        page,
        sorted,
        columns,
        pageSize,
        useGroupBy,
        useLocalTime,
        speciesListId,
        localTimezone,
    } = useSelector(state => state.queryToolState);


    return (getAllColumnData = false) => {
        // This definition has a really weird issue where when we compare these definitions
        // in `TableTitle.componentDidUpdate`, because when we save the definition into
        // SavedQuery, the order of the object keys get reordered, so we have to use the
        // same order as the DB here, including the fields under columns.
        // This works for now, and we could also solve this by sorting by object key
        // but we would also have to sort recursively, and that might affect performance
        return {
            page,
            sorted,
            columns: columns.map(x => {
                if (getAllColumnData) {
                    return x.data;
                }

                const fields = ['guid', 'name', 'title', 'filters', 'aggregate'];
                const result = {};

                fields.forEach(field => {
                    if (x.data[field] !== undefined) result[field] = x.data[field]
                });

                return result;
            }),
            pageSize,
            useGroupBy,
            useLocalTime,
            speciesListId: speciesListId ?? null,
            localTimezone: useLocalTime ? localTimezone : null,
        }
    }
};

export const useQueryData = () => {
    const dispatch = useDispatch();
    const getDefinition = useGetDefinition();

    const {count: stateCount} = useSelector(state => state.queryToolState);

    return (withCount = true) => {
        const definition = getDefinition(true);

        // Reset page back to first page on new queries
        if (withCount) {
            definition.page = 0;
        }

        QueryToolService.QueryData(definition, withCount)
            .then(({result, count = null}) => {
                dispatch({type: SET_DATA, data: result});
                dispatch({type: SET_COUNT, count: count ?? stateCount});
                dispatch({type: SET_PAGE, page: definition.page});
            });
    }
}

export const useGetPlaceholderGuid = () => {
    const {newColumns} = useSelector(state => state.queryToolState);

    return newColumns[0]?.data?.guid;
}

export const useHasColumns = () => {
    const {columns} = useSelector(state => state.queryToolState);

    return columns.length > 0 && columns.some((x) => x?.data?.columnSourceGroupName !== undefined);
}

// Since the hook is used in multiple components, useEffect will run multiple times
// unless we use this pattern
// TODO: see if this is a reoccurring issue and maybe create an EffectHook for this
const hasRunEffectForInsertingColumns = {
    value: false,
};

export const useInsertColumns = () => {
    const dispatch = useDispatch();
    const updateColumn = useUpdateColumn();
    const createColumnObject = useCreateColumnObject();

    const {columns, newColumns, isInsertingColumns} = useSelector(state => state.queryToolState);

    const [firstColumn, setFirstColumn] = useState(null);

    useUpdateEffect(() => {
        if (isInsertingColumns && firstColumn && !hasRunEffectForInsertingColumns.value) {
            const placeholder = newColumns.find((x) => NAME_PLACEHOLDER === x.data.name);

            if (placeholder) updateColumn(placeholder.data.guid, firstColumn, 'newColumns');

            setFirstColumn(null);
            dispatch({type: FINISH_INSERT_COLUMNS});
            hasRunEffectForInsertingColumns.value = true;
        } else if (!isInsertingColumns) {
            hasRunEffectForInsertingColumns.value = false;
        }

        // Resetting the global ref on component unmount
        return () => {
            hasRunEffectForInsertingColumns.value = false;
        };
    }, [isInsertingColumns, firstColumn])

    return (guid, columnData) => {
        const placeholder = newColumns.find((x) => NAME_PLACEHOLDER === x.data.name);
        const columnsClone = ObjectUtils.DeepClone(columns);
        const index = columnsClone.findIndex((x) => guid === x.data.guid);
        const nextIndex = index + (newColumns.length || 1);

        const [first, ...rest] = columnData;
        if (placeholder) columnData = rest;
        setFirstColumn(first);
        const newColumnObjects = columnData.map(x => createColumnObject(x));

        // Append after current column
        columnsClone.splice(nextIndex, 0, ...newColumnObjects);

        dispatch({
            type: INSERT_COLUMNS,
            columns: columnsClone,
            newColumns: [...newColumns, ...newColumnObjects]
        });
    }
}

export const useUpdateColumn = () => {
    const dispatch = useDispatch();
    const createColumnObject = useCreateColumnObject();

    const {columns, newColumns} = useSelector(state => state.queryToolState);

    const updateColumn = (guid, data, columnName = 'columns', isStale = false) => {
        const columnData = columnName === 'columns' ? columns : newColumns;
        const updatedColumns = [...columnData].map((x) => {
            if (x.data.guid === guid) {
                data = {...data, guid};
                return createColumnObject(data);
            }

            return x;
        });

        // update both newColumns and columns
        if (columnName === 'newColumns') {
            dispatch({type: SET_NEW_COLUMNS, newColumns: updatedColumns});
            updateColumn(guid, data);
        } else {
            dispatch({type: SET_COLUMNS, columns: updatedColumns});
        }
        dispatch({type: SET_IS_STALE, isStale});
    }

    return updateColumn;
}

export const useDeleteColumns = () => {
    const dispatch = useDispatch();
    const updateColumn = useUpdateColumn();
    const getPlaceholderGuid = useGetPlaceholderGuid();
    const queryData = useQueryData();

    const {columns, newColumns, isDeletingColumns} = useSelector(state => state.queryToolState);

    const [shouldResetPlaceholder, setShouldResetPlaceholder] = useState(false);
    const [shouldUpdate, setShouldUpdate] = useState(false);

    useEffect(() => {
        if (isDeletingColumns) {
            if (shouldResetPlaceholder) {
                updateColumn(getPlaceholderGuid, {name: NAME_PLACEHOLDER}, 'newColumns')
            }

            if (shouldUpdate) {
                queryData();
            }

            setShouldResetPlaceholder(false);
            setShouldUpdate(false);
            dispatch({type: FINISH_DELETE_COLUMNS});
        }
    }, [isDeletingColumns])

    return (guids, shouldResetPlaceholder = false, shouldUpdate = false) => {
        let [first, ...rest] = guids;

        if (shouldResetPlaceholder) {
            guids = rest;
        }

        setShouldResetPlaceholder(shouldResetPlaceholder);
        setShouldUpdate(shouldUpdate);
        dispatch({
            type: DELETE_COLUMNS,
            columns: columns.filter(x => !guids.includes(x.data.guid)),
            newColumns: newColumns.filter(x => !guids.includes(x.data.guid)),
        })
    }
}

export const useSortColumns = () => {
    const dispatch = useDispatch();
    const queryData = useQueryData();

    const {sorted} = useSelector(state => state.queryToolState);

    return (e, field) => {
        let existingField = null;

        const defaultParams = {
            id: field,
            desc: false,
        };

        const sortedClone = [...sorted].map(x => {
            if (x.id === field) {
                existingField = {...x, desc: !x.desc};

                return existingField;
            }

            return x;
        });

        if (e.shiftKey) {
            // Multi sort
            if (existingField) {
                dispatch({type: SET_SORTED, sorted: sortedClone});
            } else {
                // append
                dispatch({type: SET_SORTED, sorted: [...sortedClone, defaultParams]});
            }
        } else {
            // Single sort
            if (existingField) {
                dispatch({type: SET_SORTED, sorted: [existingField]});
            } else {
                // replace
                dispatch({type: SET_SORTED, sorted: [defaultParams]});
            }
        }
        queryData();
    }
}

export const useCreateColumnObject = () => {
    const numericSort = (a, b) => {
        return Number(a) - Number(b);
    }

    return (columnData) => {
        // ensure we have a guid
        // (if we already do we are updating)
        columnData = {
            ...columnData,
            guid: columnData.guid || GuidUtils.Generate()
        };

        return {
            data: columnData,
            accessor: columnData.guid,
            sortMethod: (columnData.dataType === 'int' || columnData.dataType === 'float') ? numericSort : undefined, // undefined uses default sort method
            Header: () => <Header data={columnData} />,
        }
    }
}

export const useFindColumnById = () => {
    const {allColumns} = useSelector(state => state.queryToolState);

    return (id) => {
        return allColumns
            .map(x => x.ColumnSource)
            .flat(1)
            .find((x) => id == x.name);
    }
}

export const useUpdateFilter = () => {
    const dispatch = useDispatch();
    const queryData = useQueryData();

    const {isUpdatingFilter} = useSelector(state => state.queryToolState);

    useEffect(() => {
        if (isUpdatingFilter) {
            queryData();
            dispatch({type: FINISH_SET_FILTER});
        }
    }, [isUpdatingFilter])

    return (name, value) => {
        dispatch({type: SET_FILTER, name, value});
    }
}

export const useClearFilters = () => {
    const dispatch = useDispatch();
    const queryData = useQueryData();
    const createColumnObject = useCreateColumnObject();

    const {isResettingFilters, columns} = useSelector(state => state.queryToolState);

    useEffect(() => {
        if (isResettingFilters) {
            queryData();
            dispatch({type: FINISH_RESET_FILTERS});
        }
    }, [isResettingFilters])

    return () => {
        // TODO: should we set these back to the defaults provided by the API
        // instead of clearing them completely?
        const columnsClone = [...columns].map((x) => (
            createColumnObject({...x.data, filters: []})
        ));

        dispatch({type: RESET_FILTERS, columns: columnsClone});
    }
}

export const useChangeFavorite = () => {
    const dispatch = useDispatch();
    const queryData = useQueryData();
    const createColumnObject = useCreateColumnObject();
    const findColumnById = useFindColumnById();
    const patchQueryParams = usePatchQueryParams();

    const {isChangingFavorites, skipReload, favorite} = useSelector(state => state.queryToolState);

    const onSavedQueryIdChange = (newSavedQueryId) => {
        patchQueryParams({'savedQueryId': newSavedQueryId});
    }

    useEffect(() => {
        if (isChangingFavorites) {
            onSavedQueryIdChange?.(favorite?.savedQueryId);
            if(!skipReload) { queryData(); }
            dispatch({type: FINISH_CHANGING_FAVORITES});
        }
    }, [isChangingFavorites])

    return (favorite, definition, skipReload = false) => {
        dispatch({
            type: CHANGE_FAVORITE,
            favorite,
            definition,
            skipReload,
            columns: definition?.columns.map(column => createColumnObject({
                ...findColumnById(column.name),
                ...column,
            })) ?? [],
        })
    }
}
